import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { peekQueryClient } from "@/queries/client";
import { invalidateScoped, patchScoped } from "@/queries/invalidation";
import { workspaceRolesKey } from "@/queries/workspace-roles";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type { ICustomRole } from "@/types/workspace";

/**
 * The workspace role writes, and which one is in flight. Roles are read only
 * through Query (NG-2814): the list through `useWorkspaceRoles` /
 * `readWorkspaceRoles`, the permission catalogue and a role's draft through
 * their hooks. The writes here patch or invalidate the cached list, so every
 * screen that names roles shows a confirmed write.
 */
export class WorkspaceRolesStore {
  activeRoleId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The scope `slug`'s cached role reads live under, or `null` before the session resolves. */
  private scope(slug: string) {
    return queryCacheScope(slug, "workspace-roles");
  }

  /** `slug`'s cached roles list, or `undefined` before a reader loaded it. */
  private cachedRoles(slug: string): ICustomRole[] | undefined {
    const scope = this.scope(slug);
    if (!scope) return undefined;
    return peekQueryClient()?.getQueryData<ICustomRole[]>(workspaceRolesKey(scope));
  }

  /** Apply a confirmed write to `slug`'s cached roles list, if a reader loaded it. */
  private patchRoles(slug: string, update: (roles: ICustomRole[]) => ICustomRole[]): void {
    const scope = this.scope(slug);
    const client = peekQueryClient();
    if (!scope || !client) return;
    patchScoped<ICustomRole[]>(client, workspaceRolesKey(scope), update);
  }

  /**
   * Re-read the whole list after a write, because `is_default` is a verdict about the
   * *workspace*, not a property of one row (NG-1335).
   *
   * Marking one role the default clears every other, and clearing the last claim hands
   * the badge back to the built-in Standard User. A single-role response cannot carry
   * that answer, so patching it alone leaves the "Default" badge on a role that no
   * longer holds it, or on no role at all, which is the exact symptom NG-1335 is about.
   *
   * Never throws. The write already succeeded, so failing the caller here would toast
   * an error over a save that worked; a stale badge until the next read is the milder
   * failure (a failed refetch keeps the patched list and is logged by the cache).
   */
  private async refreshRolesAfterWrite(slug: string): Promise<void> {
    const scope = this.scope(slug);
    const client = peekQueryClient();
    if (!scope || !client) return;
    try {
      await invalidateScoped(client, scope, "roles", "list");
    } catch (error) {
      console.warn("Failed to refresh roles after a role write; the Default badge may be stale", {
        slug,
        error,
      });
    }
  }

  async createRole(
    slug: string,
    data: Partial<ICustomRole>,
    source: AnalyticsSource = "page",
  ): Promise<ICustomRole> {
    this.activeRoleId = "creating";
    try {
      const role = await this.rootStore.workspaceRoleService.createRole(slug, data);
      // In the list before the re-read settles, so the role page the caller
      // opens next finds it.
      this.patchRoles(slug, (roles) => [...roles, role]);
      // Before the refresh, so a failed reload doesn't drop it (NG-2063).
      track("role_created", { role_id: role.id, source });
      await this.refreshRolesAfterWrite(slug);
      return role;
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  /** NG-1901: a server-side copy of `roleId`. Same bookkeeping as
   *  `createRole`: the copy is appended, then the list re-read so every row's
   *  `can_delete` reflects one more role in the workspace. */
  async duplicateRole(
    slug: string,
    roleId: string,
    source: AnalyticsSource = "page",
  ): Promise<ICustomRole> {
    this.activeRoleId = "creating";
    try {
      const role = await this.rootStore.workspaceRoleService.duplicateRole(slug, roleId);
      this.patchRoles(slug, (roles) => [...roles, role]);
      track("role_created", { role_id: role.id, duplicated_from: roleId, source });
      await this.refreshRolesAfterWrite(slug);
      return role;
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  async updateRole(
    slug: string,
    roleId: string,
    data: Partial<ICustomRole>,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeRoleId = roleId;
    // Read before the write: `fields` is what this save changed.
    const previous = this.cachedRoles(slug)?.find((item) => item.id === roleId);
    try {
      // Legacy flag-off editor still has a Save action. Make that action an
      // explicit publish, not a direct edit of the RBAC row: the same API
      // boundary protects old bundles and the flagged draft editor alike.
      await this.rootStore.workspaceRoleService.updateRoleDraft(slug, roleId, data);
      await this.rootStore.workspaceRoleService.publishRoleDraft(slug, roleId);
      track("role_updated", { role_id: roleId, fields: changedFields(data, previous), source });
      await this.refreshRolesAfterWrite(slug);
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  /** A role editor published `roleId`'s draft: re-read the list, which now shows it. */
  rolePublished(slug: string): Promise<void> {
    return this.refreshRolesAfterWrite(slug);
  }

  /** A role's "Can be assigned as" answers changed (NG-3896). Goal Lead lives in
   *  the role's permissions, so the list's copy of them is re-read. */
  roleEligibilityChanged(slug: string): Promise<void> {
    return this.refreshRolesAfterWrite(slug);
  }

  /** Make *roleId* the workspace's default role (NG-1902).
   *
   *  Goes through the badge-only route, never `updateRole`: that one publishes
   *  the role's draft, which would take an admin's unpublished edits live with
   *  it. Not optimistic, deliberately: `is_default` is a workspace verdict the
   *  server resolves (`resolve_default_role`), so the badge moves when the
   *  response says it landed, and its moving is the sign the write persisted.
   *  One holder only, so the response row replaces its own and every other row
   *  drops the badge; no re-list needed. A failure rejects for the caller's
   *  toast. */
  async setDefaultRole(
    slug: string,
    roleId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const updated = await this.rootStore.workspaceRoleService.setDefaultRole(slug, roleId);
    track("role_updated", { role_id: roleId, fields: ["is_default"], source });
    this.patchRoles(slug, (roles) =>
      roles.map((role) => (role.id === updated.id ? updated : { ...role, is_default: false })),
    );
  }

  async deleteRole(slug: string, roleId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeRoleId = roleId;
    try {
      await this.rootStore.workspaceRoleService.deleteRole(slug, roleId);
      track("role_deleted", { role_id: roleId, source });
      this.patchRoles(slug, (roles) => roles.filter((role) => role.id !== roleId));
      await this.refreshRolesAfterWrite(slug);
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }
}
